/**
 * BuildAlternativeModal — self-contained Claude-drafted MVP brief modal.
 *
 * Props:
 *   slug         (string)   — G2 product slug, e.g. "quickbooks-online"
 *   productName  (string)   — display name, e.g. "QuickBooks Online"
 *   onClose      (fn)       — called when the user dismisses the modal
 *
 * Manages its own lifecycle: kicks off POST /api/g2/{slug}/build-alternative
 * on mount, handles loading / error / parse-error states, lets the user
 * Save to Library, and exposes CSV / PDF download links once saved.
 *
 * Used by both G2PainIndex (dashboard widget) and WedgePanel (TrustCard).
 */
import React, { useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BuildAlternativeModal = ({ slug, productName, onClose }) => {
  const [briefData, setBriefData] = useState({ product_name: productName });
  const [briefLoading, setBriefLoading] = useState(true);
  const [briefError, setBriefError] = useState("");
  const [savedBriefId, setSavedBriefId] = useState(null);
  const [savingBrief, setSavingBrief] = useState(false);
  const [draftedMs, setDraftedMs] = useState(null);    // wall-clock duration of the draft
  const [linkCopied, setLinkCopied] = useState(false);

  // Kick off the brief draft + check if already saved.
  useEffect(() => {
    let cancelled = false;
    setBriefLoading(true);
    setBriefError("");
    const startedAt = Date.now();

    // Check if a brief for this slug is already saved.
    axios.get(`${API}/briefs`, { timeout: 8000 })
      .then((r) => {
        if (cancelled) return;
        const existing = (r.data?.briefs || []).find((b) => b.product_slug === slug);
        if (existing) setSavedBriefId(existing.id);
      })
      .catch(() => {});

    // Draft the brief via Claude.
    axios.post(`${API}/g2/${slug}/build-alternative`, {}, { timeout: 90000 })
      .then((r) => {
        if (!cancelled) {
          setBriefData(r.data);
          setDraftedMs(Date.now() - startedAt);
        }
      })
      .catch((e) => {
        if (!cancelled) setBriefError(e?.response?.data?.detail || e?.message || "Failed to draft brief.");
      })
      .finally(() => { if (!cancelled) setBriefLoading(false); });

    return () => { cancelled = true; };
  }, [slug]);

  const saveBriefToLibrary = async () => {
    if (!briefData?.brief || !slug) return;
    setSavingBrief(true);
    try {
      const r = await axios.post(`${API}/briefs`, {
        product_slug: slug,
        product_name: briefData.product_name,
        brief: briefData.brief,
        g2_url: briefData.g2_url,
        complaints_count: briefData.complaints_count,
      }, { timeout: 8000 });
      setSavedBriefId(r.data?.id || true);
    } catch {
      // Silent fail — user can retry
    } finally {
      setSavingBrief(false);
    }
  };

  const isSaved = !!savedBriefId;
  const downloadHref = (fmt) => savedBriefId && savedBriefId !== true
    ? `${API}/briefs/${savedBriefId}/export.${fmt}`
    : "#";

  return (
    <div
      className="fixed inset-0 z-50 bg-zinc-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="build-alt-modal"
    >
      <div
        className="bg-white max-w-2xl w-full rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-violet-600">⚡ MVP Brief — Drafted by Claude</div>
            <h3 className="text-lg font-semibold text-zinc-900 mt-0.5">
              An alternative to {briefData?.product_name || productName}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-900 text-xl px-2"
            data-testid="build-alt-close"
          >
            ✕
          </button>
        </header>

        <div className="p-6 space-y-5 text-sm" data-testid="build-alt-body">
          {briefLoading && (
            <div className="flex items-center gap-3 text-zinc-600">
              <span className="inline-block w-3 h-3 rounded-full bg-violet-600 animate-pulse" />
              Claude is reading the complaints and drafting your launch brief…
              <span className="text-zinc-400 text-xs">(usually 15-30s)</span>
            </div>
          )}
          {briefError && (
            <div className="text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs">
              {briefError}
            </div>
          )}
          {briefData?.brief && (
            <>
              <div data-testid="brief-positioning">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-1">Positioning</div>
                <div className="text-zinc-900 font-medium">{briefData.brief.positioning}</div>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-1">Target persona</div>
                <div className="text-zinc-800">{briefData.brief.target_persona}</div>
              </div>
              <div data-testid="brief-features">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2">Top 3 must-have features</div>
                <ol className="space-y-2">
                  {(briefData.brief.top_3_features || []).map((f, i) => (
                    <li key={i} className="bg-violet-50 border border-violet-100 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <span className="font-mono text-[10px] text-violet-600 mt-0.5">{i + 1}</span>
                        <span className="text-zinc-900">{f}</span>
                      </div>
                      {briefData.brief.feature_evidence?.[i] && (
                        <blockquote className="mt-2 text-[11px] text-zinc-600 border-l-2 border-violet-300 pl-2 italic">
                          evidence: "{briefData.brief.feature_evidence[i]}"
                        </blockquote>
                      )}
                    </li>
                  ))}
                </ol>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-1">Suggested pricing</div>
                  <div className="text-zinc-900 font-semibold">{briefData.brief.suggested_pricing}</div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-1">Complaints analyzed</div>
                  <div className="text-zinc-900 font-semibold">{briefData.complaints_count}</div>
                </div>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-1">GTM angle</div>
                <div className="text-zinc-800 italic">"{briefData.brief.gtm_angle}"</div>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-1">First ad headline</div>
                <div className="text-zinc-900 text-base font-semibold bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="brief-headline">
                  "{briefData.brief.first_ad_headline}"
                </div>
              </div>
              {briefData.g2_url && (
                <a
                  href={briefData.g2_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block text-[10px] font-mono uppercase tracking-[0.15em] text-violet-700 hover:underline pt-2"
                >
                  Read the raw complaints on G2 ↗
                </a>
              )}

              {/* Share card — only after the brief is saved + has a public id */}
              {savedBriefId && savedBriefId !== true && (() => {
                const pageUrl = `${window.location.origin}/brief/${savedBriefId}`;
                const tweetText = `${briefData.brief.first_ad_headline || briefData.brief.positioning} — full MVP brief:`;
                const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(pageUrl)}`;
                const copy = async () => {
                  try { await navigator.clipboard.writeText(pageUrl); } catch { /* ignore */ }
                  setLinkCopied(true);
                  setTimeout(() => setLinkCopied(false), 1600);
                };
                return (
                  <div className="mt-4 bg-zinc-900 rounded-xl p-4 text-white" data-testid="brief-share-card">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 mb-2">
                      {draftedMs != null
                        ? `Brief drafted in ${Math.round(draftedMs / 1000)}s — share it?`
                        : "Share this brief"}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <a href={tweetUrl} target="_blank" rel="noopener noreferrer"
                         className="px-3 py-1.5 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-[12px] font-semibold"
                         data-testid="brief-share-tweet">🐦 Tweet this brief</a>
                      <button onClick={copy}
                              className="px-3 py-1.5 rounded-full bg-white text-zinc-900 hover:bg-zinc-100 text-[12px] font-semibold"
                              data-testid="brief-share-copy">
                        {linkCopied ? "✓ Copied" : "🔗 Copy public link"}
                      </button>
                    </div>
                  </div>
                );
              })()}
            </>
          )}
          {briefData?.parse_error && (
            <div className="text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs">
              Couldn't parse Claude's reply as JSON. Raw output:
              <pre className="mt-2 whitespace-pre-wrap text-[11px] text-zinc-700">{briefData.raw}</pre>
            </div>
          )}
        </div>

        {briefData?.brief && (
          <footer className="px-6 py-3 border-t border-zinc-100 flex flex-wrap items-center justify-between gap-3 bg-zinc-50/60 sticky bottom-0" data-testid="brief-actions">
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">
              {isSaved ? "✓ Saved to your library" : "Save this brief for later"}
            </div>
            <div className="flex items-center gap-2">
              {isSaved && savedBriefId !== true ? (
                <>
                  <a href={downloadHref("csv")} download
                     className="px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.15em] rounded-full bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50"
                     data-testid="brief-download-csv">⬇ CSV</a>
                  <a href={downloadHref("pdf")} download
                     className="px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.15em] rounded-full bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50"
                     data-testid="brief-download-pdf">⬇ PDF</a>
                  <span className="px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.15em] rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700">
                    ★ Saved
                  </span>
                </>
              ) : (
                <button
                  onClick={saveBriefToLibrary}
                  disabled={savingBrief || isSaved}
                  className="px-4 py-1.5 text-[11px] font-mono uppercase tracking-[0.15em] rounded-full bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 transition-colors"
                  data-testid="brief-save-btn"
                >
                  {savingBrief ? "Saving…" : isSaved ? "★ Saved" : "★ Save to library"}
                </button>
              )}
            </div>
          </footer>
        )}
      </div>
    </div>
  );
};

export default BuildAlternativeModal;
